import * as React from 'react';
import { Link } from 'react-router-dom';

import styled, { css } from 'styled-components';

import Typography from '@components/basics/typography';
import { Translate } from 'react-localize-redux';
import Flex from '../../layout/flex';

interface NewMenuItemProps {
  className?: string;
  children?: any;
  accent?: boolean;
  to?: string;
  disabled?: boolean;
  soon?: boolean;
  active?: boolean;
  onClick?: () => void;
  max?: number;
  filled?: number | null;
  artistConnectedCount?: number;
  icon?: React.ReactElement;
}

export const NewMenuItem: React.FC<NewMenuItemProps> = (props) => {
  const isDisabled =
    props.disabled ||
    ((props.artistConnectedCount || 1) < 3 && props.to === '/user/label');

  const NewMenuItemContainer: React.FC = (myProps) => {
    if (props.to && !isDisabled) {
      return (
        <Link className={props.className} to={props.to}>
          {myProps.children}
        </Link>
      );
    }

    return (
      <span className={`${props.className} ${isDisabled ? 'disabled' : ''}`}>
        {myProps.children}
      </span>
    );
  };

  return (
    <NewMenuItemContainer>
      <Flex alignItems="center" gap={10}>
        {props.icon}
        <Typography
          className="menu-item-text"
          type="link"
          onClick={() =>
            props.onClick && !isDisabled ? props.onClick() : null
          }
        >
          {props.children}
        </Typography>
      </Flex>
      {props.soon ? (
        <Typography className="soon" weight="700" type="small">
          <Translate id="DASHBOARD.NEW_DASHBOARD.USER.MENU.SOON" />
        </Typography>
      ) : null}
      {(props.artistConnectedCount || 1) < 3 && props.to === '/user/label' && (
        <ProgressContainer>
          {Array.from(Array(props.max).keys()).map((index) => (
            <ProgressItem
              key={index}
              filled={
                !!(
                  props.artistConnectedCount &&
                  index < props.artistConnectedCount
                )
              }
            />
          ))}
        </ProgressContainer>
      )}
    </NewMenuItemContainer>
  );
};

export default styled(NewMenuItem)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  margin-bottom: 10px;

  ${(props) =>
    props.soon
      ? css`
          padding-right: 40px;
        `
      : null}

  &:last-child {
    margin-bottom: 0;
  }

  &.disabled {
    .menu-item-text {
      opacity: 0.5;
      pointer-events: none;
      &:hover {
        opacity: 0.5;
      }
    }
  }

  ${(props) =>
    !props.soon
      ? css`
          &:hover {
            .menu-item-text {
              opacity: 1;
            }
          }
        `
      : null}

  .menu-item-text {
    flex-grow: 0;
    flex-shrink: 0;
    font-size: ${({ theme }) => theme.typography.h4};
    font-weight: 700;

    ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
      opacity: 0.5;
      transition: color
          ${({ theme }) =>
            `${theme.transitions.duration} ${theme.transitions.type}`},
        opacity
          ${({ theme }) =>
            `${theme.transitions.duration} ${theme.transitions.type}`};
    }

    ${(props) =>
      props.accent
        ? css`
            opacity: 1;
            color: ${({ theme }) => theme.colors.accent};

            &:hover {
              color: ${({ theme }) => theme.colors.accentHover};
            }
          `
        : null}

    ${(props) =>
      props.active
        ? css`
            @media ${({ theme }) => theme.device.tablet} {
              opacity: 1;
            }
          `
        : null}
  }

  ${(props) =>
    props.soon
      ? css`
          .soon {
            padding: 4px 6px;
            font-size: 10px;
            position: absolute;
            right: 0;
            top: 50%;
            transform: translate(0, -50%);
            color: ${({ theme }) => theme.colors.hintText};
            background: ${({ theme }) => theme.colors.hint};
            border-radius: 12px;
            opacity: 1;
            ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
              transform: translate(0, -50%) scale(0.75);
              font-size: 12px;
            }
          }
        `
      : null}
`;

const ProgressContainer = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
`;

const ProgressItem = styled.span<{ filled: boolean }>`
  display: inline-block;
  width: 19px;
  height: 3px;
  background-color: ${({ filled, theme }) =>
    filled ? theme.colors.blueColor : theme.colors.blogBg};

  &:first-child {
    border-radius: 10px 0 0 10px;
  }

  &:last-child {
    border-radius: 0 10px 10px 0;
  }
`;
